/**
 * F11 — typed wrappers for the admin venue-claim endpoints.
 *
 * Endpoints covered:
 *   GET    /admin/venue-claims                                        → queue list (+ count mode)
 *   GET    /admin/venue-claims/:id                                    → detail (claim + venue + account + claimant + docs)
 *   POST   /admin/venue-claims/:id/approve                            → approve (optional adminOverrideNoDocs + reason)
 *   POST   /admin/venue-claims/:id/reject                             → reject with reason
 *   POST   /admin/venue-claims/:id/revoke                             → revoke an already-approved claim
 *   PATCH  /admin/venue-claims/:id/site-visit-notes                   → admin notes
 *   GET    /admin/venue-claims/:id/documents/:docId/download-url      → 5-min presigned GET
 */
import type {
  AdminPageMeta,
  ApproveVenueClaimInput,
  RejectVenueClaimInput,
  VenueClaim,
  VenueClaimDocument,
  VenueClaimEntityStatus,
  VenueClaimSiteVisitNotesInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient.js';

// ─── Response envelopes ────────────────────────────────────────────────────────

export interface VenueClaimVenueRef {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
}

export interface VenueClaimAccountRef {
  readonly id: string;
  readonly displayName: string;
}

export interface VenueClaimClaimantRef {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
}

export interface VenueClaimQueueRow extends VenueClaim {
  readonly venue: VenueClaimVenueRef | null;
  readonly account: VenueClaimAccountRef | null;
  readonly claimant: VenueClaimClaimantRef | null;
}

export interface VenueClaimQueueResponse {
  readonly items: readonly VenueClaimQueueRow[];
  readonly nextCursor: string | null;
  /** Present when `page` is supplied (page-numbered pagination). */
  readonly pagination?: AdminPageMeta;
}

export interface VenueClaimDetailResponse {
  readonly claim: VenueClaim;
  readonly venue: VenueClaimVenueRef | null;
  readonly account: VenueClaimAccountRef | null;
  readonly claimant: VenueClaimClaimantRef | null;
  readonly documents: readonly VenueClaimDocument[];
}

export interface VenueClaimDocumentDownloadResponse {
  readonly url: string;
  readonly expiresAt: string;
}

// ─── List options ──────────────────────────────────────────────────────────────

export interface VenueClaimQueueOpts {
  readonly status?: VenueClaimEntityStatus | 'all';
  /** Narrow the queue to one venue's claims (server-side `?venueId=` filter). */
  readonly venueId?: string;
  readonly cursor?: string;
  readonly limit?: number;
  readonly count?: boolean;
  /** 1-based page index (page-numbered pagination). */
  readonly page?: number;
  /** Page size (default 25 server-side, max 100). */
  readonly perPage?: number;
}

// ─── API surface ───────────────────────────────────────────────────────────────

export const adminVenueClaimApi = {
  /** GET /admin/venue-claims — paginated queue (FIFO for pending). */
  listQueue: (opts?: VenueClaimQueueOpts): Promise<VenueClaimQueueResponse> =>
    adminFetch<VenueClaimQueueResponse>(
      `/admin/venue-claims${buildQuery({
        status: opts?.status,
        venueId: opts?.venueId,
        cursor: opts?.cursor,
        limit: opts?.limit,
        page: opts?.page,
        perPage: opts?.perPage,
      })}`,
    ),

  /** GET /admin/venue-claims/:id — full claim + venue + account + claimant + docs. */
  detail: (id: string): Promise<VenueClaimDetailResponse> =>
    adminFetch<VenueClaimDetailResponse>(`/admin/venue-claims/${id}`),

  /** POST /admin/venue-claims/:id/approve. */
  approve: (id: string, body: ApproveVenueClaimInput = {}): Promise<{ data: unknown }> =>
    adminFetch(`/admin/venue-claims/${id}/approve`, {
      method: 'POST',
      body: body as Record<string, unknown>,
    }),

  /** POST /admin/venue-claims/:id/reject — reason min 10 chars. */
  reject: (id: string, body: RejectVenueClaimInput): Promise<{ data: unknown }> =>
    adminFetch(`/admin/venue-claims/${id}/reject`, {
      method: 'POST',
      body: body as Record<string, unknown>,
    }),

  /** POST /admin/venue-claims/:id/revoke — only allowed when status='approved'. */
  revoke: (id: string, body: RejectVenueClaimInput): Promise<{ data: unknown }> =>
    adminFetch(`/admin/venue-claims/${id}/revoke`, {
      method: 'POST',
      body: body as Record<string, unknown>,
    }),

  /** PATCH /admin/venue-claims/:id/site-visit-notes — admin internal notes. */
  updateSiteVisitNotes: (
    id: string,
    body: VenueClaimSiteVisitNotesInput,
  ): Promise<{ data: unknown }> =>
    adminFetch(`/admin/venue-claims/${id}/site-visit-notes`, {
      method: 'PATCH',
      body: body as Record<string, unknown>,
    }),

  /** GET /admin/venue-claims/:id/documents/:docId/download-url — 5-min presigned GET. */
  getDocumentDownloadUrl: (
    claimId: string,
    docId: string,
    reason?: string,
  ): Promise<VenueClaimDocumentDownloadResponse> =>
    adminFetch<VenueClaimDocumentDownloadResponse>(
      `/admin/venue-claims/${claimId}/documents/${docId}/download-url${buildQuery({ reason })}`,
    ),
};
